import {
  Box,
  Container,
  Heading,
  Link,
  List,
  ListItem,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const SUPPORT_EMAIL = "support@fursatook.com";

const renderInlineText = (text) => {
  const parts = text.split(SUPPORT_EMAIL);
  if (parts.length === 1) {
    return text;
  }

  return parts.reduce((acc, part, index) => {
    if (index > 0) {
      acc.push(
        <Link
          key={`email-${index}`}
          color="teal.500"
          href={`mailto:${SUPPORT_EMAIL}`}
          dir="ltr"
          display="inline"
        >
          {SUPPORT_EMAIL}
        </Link>
      );
    }
    if (part) {
      acc.push(part);
    }
    return acc;
  }, []);
};

const LegalSection = ({ section, depth = 0 }) => {
  const HeadingTag = depth === 0 ? "h2" : "h3";
  const headingSize = depth === 0 ? "lg" : "md";

  return (
    <Box as="section" id={section.id} mb={6}>
      <Heading as={HeadingTag} size={headingSize} mb={2}>
        {section.title}
      </Heading>
      {section.paragraphs?.map((paragraph, index) => (
        <Text key={`p-${section.id}-${index}`} mb={3} lineHeight="tall">
          {renderInlineText(paragraph)}
        </Text>
      ))}
      {section.list?.length > 0 && (
        <List spacing={2} mb={3} styleType="disc" pl={5}>
          {section.list.map((item, index) => (
            <ListItem key={`li-${section.id}-${index}`} lineHeight="tall">
              {renderInlineText(item)}
            </ListItem>
          ))}
        </List>
      )}
      {section.subsections?.map((subsection) => (
        <LegalSection
          key={subsection.id}
          section={subsection}
          depth={depth + 1}
        />
      ))}
    </Box>
  );
};

const LegalDocumentPage = ({ documentKey }) => {
  const { t } = useTranslation("legal");
  const document = t(documentKey, { returnObjects: true });

  if (!document || typeof document !== "object") {
    return null;
  }

  return (
    <Container maxW="container.md" mt={5} mb={5}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Box p={5} bg="white" borderRadius="md" boxShadow="md">
        <Heading as="h1" size="xl" mb={4}>
          {document.title}
        </Heading>
        <Text mb={4} color="gray.600">
          {document.lastUpdatedLabel}: {document.lastUpdated}
        </Text>
        {document.intro?.map((paragraph, index) => (
          <Text key={`intro-${index}`} mb={3} lineHeight="tall">
            {renderInlineText(paragraph)}
          </Text>
        ))}
        <VStack align="stretch" spacing={0} mt={4}>
          {document.sections?.map((section) => (
            <LegalSection key={section.id} section={section} />
          ))}
        </VStack>
      </Box>
    </Container>
  );
};

export default LegalDocumentPage;
